import { c as _c } from "react/compiler-runtime";
import { useContext, useState } from 'react';
import styled, { css } from 'styled-components';
import { Grid, GridUnit } from '@hookdeck/theme';
import { Rule } from '../../../../../../../../typings/Webhook.interface';
import { APIWebhook } from '../../../../../../../../typings/Webhook.interface';
import { RULE_TYPES } from '../../../../../utils/rules';
import Button from '../../../../common/base/Button';
import { StyledCard } from '../../../../common/base/Card';
import Icon, { StyledIcon } from '../../../../common/base/Icon';
import Text from '../../../../common/base/Text';
import Tooltip from '../../../../common/base/Tooltip';
import { useDialog } from '../../../../common/Dialog';
import Dropdown from '../../../../common/Dropdown';
import { Div } from '../../../../common/helpers/StyledUtils';
import { PreviewBucket } from '../../../../common/Histogram/PreviewHistogram';
import { useTruncatable } from '../../../../common/Truncatable';
import DestinationCard from '../DestinationCard';
import { getOrderedRules } from '../Forms/rules';
import { group_by_options, ResourcesContext } from '../ResourcesContext';
import SourceCard from '../SourceCard';
import ConnectionPreview, { is_filter_rule_changed } from './ConnectionPreview';
import DestinationPreview from './DestinationPreview';
import MetricsPreview from './MetricsPreview';
import SourcePreview from './SourcePreview';
const StyledLines = styled.div<{
  hover_index: number;
  index: number;
  selected: boolean;
  has_more: boolean;
  disabled?: boolean;
  paused?: boolean;
  group_by?: keyof typeof group_by_options;
}>(({
  theme,
  hover_index,
  index,
  has_more,
  disabled,
  selected,
  group_by,
  paused
}) => {
  const is_hovered = hover_index === index;
  const primary_color = theme.colors.on.neutral.primary;
  const neutral_color = theme.colors.outline.neutral_3;
  const hover_color = selected || is_hovered ? primary_color : theme.colors.outline.hover.neutral;
  const badge_color = selected || is_hovered ? '#ffffff' : theme.colors.on.neutral.tertiary_neutral;
  const dashed_line = `1px dashed ${is_hovered ? primary_color : neutral_color}`;
  const solid_line = `1px solid ${is_hovered ? primary_color : neutral_color}`;
  const border_line = paused || disabled ? dashed_line : solid_line;
  if (group_by === 'destination') {
    return css`
      height: 32px;
      display: flex;
      align-items: center;
      & > div:first-child {
        cursor: pointer;
        height: 0;
        border-top: ${border_line};
        width: 32px;
        position: relative;
      }

      & > div:nth-child(2) {
        cursor: pointer;
        height: 100%;
        flex-grow: 1;
        display: flex;
        align-items: center;
        position: relative;
        z-index: 1;
        &::after {
          content: '';
          width: 100%;
          height: 0;
          border-top: ${border_line};
        }

        ${(disabled || paused) && hover_index !== index && css`
          ${StyledConnectionBadge} {
            span {
              color: ${theme.colors.on.neutral.disabled};
            }
          }
        `}

        ${is_hovered && css`
          ${StyledConnectionBadge} {
            background-color: ${hover_color};
            border-color: ${hover_color};
            span {
              color: ${badge_color};
            }
          }
        `}
      }

      & > div:nth-child(3) {
        position: relative;
        cursor: pointer;
        z-index: 0;

        & > div:first-child {
          position: relative;
          height: 0;
          border-top: ${border_line};
          width: 32px;
          ${index > 0 && css`
            visibility: hidden;
          `}
          ${is_hovered && css`
            z-index: 2;
          `}
        }
        & > div:nth-child(2) {
          ${hover_index > index && css`
            z-index: 2;
          `}
          position: absolute;
          left: 5px;
          top: 0;
          height: 42px;
          width: 32px;
          border-top-left-radius: 8px;
          border-top: ${border_line};
          border-top-color: ${hover_index > index ? primary_color : neutral_color};
          border-left: ${solid_line};
          border-left-color: ${hover_index > index ? primary_color : neutral_color};
          ${!has_more && css`
            visibility: hidden;
          `}
          ${index > 0 && css`
            ${is_hovered && css`
              z-index: 2;
            `}
            left: 0;
            visibility: visible;
            border-top: ${border_line};
            border-left: ${border_line};
            height: 42px;
            margin-left: 6px;
            top: 1px;
            transform: rotate(180deg) translateX(100%) translateY(100%);
          `}
        }
        & > div:nth-child(3) {
          cursor: pointer;
          ${hover_index > index && hover_index !== index && css`
            z-index: 2;
          `}
          ${(!has_more || index === 0) && css`
            visibility: hidden;
          `}
          position: absolute;
          height: 84px;
          left: 5px;
          top: 0;
          border-left: ${solid_line};
          border-left-color: ${hover_index > index ? primary_color : neutral_color};
          transform: translateY(-50%);
        }
      }
    `;
  }
  return css`
    height: 32px;
    display: flex;
    align-items: center;
    & > div:first-child {
      position: relative;
      cursor: pointer;
      height: 0;
      border-top: ${border_line};
      width: 32px;
      ${index > 0 && css`
        visibility: hidden;
      `}
      ${is_hovered && css`
        z-index: 2;
      `}
    }
    & > div:nth-child(2) {
      position: relative;
      cursor: pointer;
      z-index: 0;
      & > div:first-child {
        height: 0;
        border-top: ${border_line};
        width: 32px;
        ${index > 0 && css`
          visibility: hidden;
        `}
      }
      & > div:nth-child(2) {
        ${hover_index > index && css`
          z-index: 2;
        `}
        position: absolute;
        left: -32px;
        top: 0;
        height: 42px;
        width: 34px;
        border-top-right-radius: 8px;
        border-top: ${border_line};
        border-top-color: ${hover_index > index ? primary_color : neutral_color};
        border-right: ${border_line};
        border-right-color: ${hover_index > index ? primary_color : neutral_color};
        ${!has_more && css`
          visibility: hidden;
          ${index === 1 && css`
            margin-left: -1px !important;
          `}
        `}
        ${index > 0 && css`
          ${is_hovered && css`
            z-index: 2;
          `}
          visibility: visible;
          border-top: ${border_line};
          border-right: ${border_line};
          height: 42px;
          width: 33px;
          margin-left: -1px;
          top: 1px;
          left: -31px;
          transform: rotate(180deg) translateX(-100%) translateY(100%);
        `}
        ${index > 1 && css`
          margin-left: 0;
          height: 44px;
          width: 32px;
          ${!has_more && css`
            left: -32px;
            width: 33px;
          `}
        `}
      }
      & > div:nth-child(3) {
        cursor: pointer;
        ${hover_index > index && hover_index !== index && css`
          z-index: 2;
        `}
        ${(!has_more || index === 0) && css`
          display: none;
        `}
        position: absolute;
        height: 84px;
        left: 2px;
        top: 0;
        border-left: ${solid_line};
        border-left-color: ${hover_index > index ? primary_color : neutral_color};
        transform: translateY(-50%);
      }
    }

    & > div:nth-child(3) {
      cursor: pointer;
      height: 100%;
      flex-grow: 1;
      display: flex;
      align-items: center;
      position: relative;
      z-index: 1;
      &::after {
        content: '';
        width: 100%;
        height: 0;
        border-top: ${border_line};
      }

      ${disabled && hover_index !== index && css`
        ${StyledConnectionBadge} {
          span {
            color: ${theme.colors.on.neutral.disabled};
          }
        }
      `}

      ${is_hovered && css`
        ${StyledConnectionBadge} {
          background-color: ${hover_color};
          border-color: ${hover_color};
          span {
            color: ${badge_color};
          }
        }
      `}
    }
  `;
});
const StyledRuleIcon = styled(Icon)(({
  theme
}) => css`
    padding: ${theme.spacing(1)} ${theme.spacing(0.5)};
    z-index: 1;
    fill: ${theme.colors.on.neutral.primary_neutral};
    color: ${theme.colors.on.neutral.primary_neutral};
  `);
const StyledArrow = styled.svg<{
  highlighted: boolean;
  selected: boolean;
}>(({
  theme,
  highlighted,
  selected
}) => css`
    fill: ${theme.colors.outline.neutral_3};
    position: absolute;
    left: -6px;
    top: 12px;
    ${(selected || highlighted) && css`
      fill: ${theme.colors.on.neutral.primary};
    `};
  `);
const StyledConnectionBadge = styled(Div)<{
  warning?: true;
}>(({
  theme,
  warning
}) => css`
    position: relative;
    display: flex;
    align-items: center;
    border-radius: 4px;
    border: 1px solid ${warning ? theme.colors.outline.warning : theme.colors.outline.neutral_3};
    background-color: ${warning ? theme.colors.surface.container.warning : theme.colors.surface.base.variant_surface};

    ${Text} {
      color: ${warning ? theme.colors.on.hue.warning : theme.colors.on.neutral.primary_neutral};
    }

    ${StyledIcon} {
      color: ${warning ? theme.colors.on.hue.warning : theme.colors.on.neutral.tertiary_neutral};
    }
  `);
const StyledConnectionWrapper = styled.div(({
  theme
}) => css`
    position: absolute;
    display: flex;
    justify-content: space-between;
    align-items: center;
    left: -${theme.spacing(2)};
    right: ${theme.spacing(8)};
    top: ${theme.spacing(1)};
  `);
const StyledCardWrapper = styled.div<{
  focused: boolean;
  highlighted: boolean;
  selected: boolean;
  disabled: boolean;
}>(({
  focused,
  theme,
  highlighted,
  disabled
}) => {
  return css`
    position: relative;

    ${StyledCard} {
      border: none;
      box-shadow: ${theme.shadows.neutral.base};

      button:hover {
        background-color: ${theme.colors.surface.base.hover.neutral};
      }

      ${disabled && css`
        background-color: ${theme.colors.surface.base.disabled};
      `}

      ${(focused || highlighted) && css`
        box-shadow: 0px 0px 0px 1px ${theme.colors.on.neutral.primary};
        z-index: 1;
        position: relative;
      `}
    }
  `;
});
const ConnectionName: React.FC<{
  name: string;
}> = t0 => {
  const $ = _c(14);
  const {
    name
  } = t0;
  let t1;
  if ($[0] !== name) {
    t1 = {
      text: name
    };
    $[0] = name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [display_name, truncated, ref] = useTruncatable(t1);
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      x: 2,
      y: 0.5
    };
    t3 = {
      maxWidth: "100%",
      position: "relative"
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== display_name || $[5] !== name || $[6] !== truncated) {
    t4 = truncated ? <Tooltip tooltip={name} side="bottom">{display_name}</Tooltip> : display_name;
    $[4] = display_name;
    $[5] = name;
    $[6] = truncated;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== name || $[9] !== t4) {
    t5 = <StyledConnectionBadge p={t2} style={t3}><Text mono={true} ellipsis={true} as="span" width={100} aria-label={name}>{t4}</Text></StyledConnectionBadge>;
    $[8] = name;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== ref || $[12] !== t5) {
    t6 = <Div ref={ref} flex={true} w={100}>{t5}</Div>;
    $[11] = ref;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};
type Props = {
  connections: APIWebhook[];
  mutate: () => void;
  timeseries_by_connection_id: {
    '2h': Record<string, PreviewBucket[]>;
    '4h': Record<string, PreviewBucket[]>;
  };
};
const StyledQuickWrapper = styled.div<{
  side: 'left' | 'right';
}>(({
  side
}) => css`
    height: 32px;
    position: absolute;
    top: -28px;
    z-index: 1;
    ${side === 'right' ? css`
          padding-left: 46px;
          right: -46px;
        ` : css`
          padding-right: 42px;
          left: -42px;
        `}
  `);
const StyledQuickAdd = styled(Button)(({
  theme
}) => css`
    a {
      height: 24px;
      width: 24px;
      background-color: ${theme.colors.surface.base.background};
      border: 1px dashed ${theme.colors.outline.neutral_3};
      border-radius: ${theme.radius.small};

      ${StyledIcon} {
        color: ${theme.colors.on.neutral.tertiary_neutral};
      }

      &:hover {
        background-color: ${theme.colors.surface.container.primary};
        border-color: ${theme.colors.on.neutral.primary};
        color: ${theme.colors.on.neutral.primary};
        box-shadow: none;

        ${StyledIcon} {
          color: ${theme.colors.on.neutral.primary};
        }
      }
    }
  `);
const GroupedConnections: React.FC<Props> = t0 => {
  const $ = _c(45);
  const {
    connections,
    timeseries_by_connection_id,
    mutate
  } = t0;
  const [hovered, setHovered] = useState(null);
  const {
    group_by
  } = useContext(ResourcesContext);
  const showDialog = useDialog();
  let t1;
  if ($[0] !== connections || $[1] !== hovered) {
    t1 = hovered && connections.findIndex(t2 => {
      const {
        id
      } = t2;
      return id === hovered.id;
    });
    $[0] = connections;
    $[1] = hovered;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const current_index = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = connection => ({
      onMouseEnter: () => setHovered({
        id: connection.id,
        selected: false
      }),
      onMouseLeave: () => setHovered(null)
    });
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const getHoverProps = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = (isDirty, values_diff) => {
      setHovered(prev => prev ? {
        ...prev,
        is_dirty: isDirty,
        values_diff
      } : null);
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleDirtyChange = t3;
  let t4;
  if ($[5] !== connections || $[6] !== hovered) {
    t4 = hovered && connections.find(t5 => {
      const {
        id: id_0
      } = t5;
      return id_0 === hovered.id;
    });
    $[5] = connections;
    $[6] = hovered;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const hovered_connection = t4;
  let t5;
  if ($[8] !== connections || $[9] !== group_by) {
    t5 = group_by === "source" ? [connections[0]] : connections;
    $[8] = connections;
    $[9] = group_by;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== group_by || $[12] !== hovered || $[13] !== hovered_connection || $[14] !== t5) {
    let t7;
    if ($[16] !== group_by || $[17] !== hovered || $[18] !== hovered_connection) {
      t7 = connection_0 => <Div m={{
        b: 4
      }} key={connection_0.id} onMouseEnter={() => setHovered({
        source_id: connection_0.source.id
      })} onMouseLeave={() => setHovered(null)}><Dropdown placement="bottom-start" parent_width={{
          min: 408
        }} onToggle={() => {
          setHovered(null);
        }} renderToggle={(opened, toggle) => <StyledCardWrapper focused={opened || !!hovered?.selected && hovered_connection?.source?.id === connection_0.source.id} disabled={!!connection_0.source.disabled_at} highlighted={!!(group_by === "source" ? !!hovered_connection && hovered_connection.source.id === connection_0.source.id : hovered && connection_0.id === hovered.id)} selected={!!(group_by === "source" ? hovered?.selected && hovered.source_id === connection_0.source.id : hovered && connection_0.id === hovered.id && hovered.selected)}><SourceCard source={connection_0.source} onClick={() => toggle(!opened)} /></StyledCardWrapper>}><SourcePreview source={connection_0.source} /></Dropdown><Div style={{
          position: "relative"
        }}><StyledQuickWrapper side="right">{group_by === "source" && hovered?.source_id === connection_0.source.id && <StyledQuickAdd ghost={true} icon="add_circle" to={`/connections/new?source_id=${connection_0.source.id}`} />}</StyledQuickWrapper></Div></Div>;
      $[16] = group_by;
      $[17] = hovered;
      $[18] = hovered_connection;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    t6 = t5.map(t7);
    $[11] = group_by;
    $[12] = hovered;
    $[13] = hovered_connection;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[20] !== t6) {
    t7 = <GridUnit size={0.25}>{t6}</GridUnit>;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== connections || $[23] !== current_index || $[24] !== group_by || $[25] !== hovered || $[26] !== hovered_connection || $[27] !== mutate || $[28] !== showDialog || $[29] !== timeseries_by_connection_id) {
    let t9;
    if ($[31] !== connections.length || $[32] !== current_index || $[33] !== group_by || $[34] !== hovered || $[35] !== hovered_connection || $[36] !== mutate || $[37] !== showDialog || $[38] !== timeseries_by_connection_id) {
      t9 = (connection_1, i) => <Div flex={true} key={connection_1.id}><Div style={{
          flexGrow: 1
        }}><Grid><GridUnit size={0.6}><Dropdown placement="bottom-start" parent_width={{
                min: 516
              }} onToggle={opened_0 => setHovered(opened_0 ? {
                id: connection_1.id,
                selected: true
              } : null)} onBeforeClose={async () => {
                if (!hovered?.is_dirty && (!hovered || !is_filter_rule_changed(hovered.values_diff))) {
                  return true;
                }
                return new Promise(resolve => {
                  showDialog(() => {
                    setHovered({
                      ...hovered,
                      is_dirty: false
                    });
                    resolve(true);
                  }, () => {
                    resolve(false);
                  }, {
                    title: "Discard unsaved changes?",
                    message: "You have unsaved changes in the connection details. Are you sure you want to discard them?",
                    submit_label: "Discard",
                    danger: true,
                    cancel_label: "Cancel"
                  });
                });
              }} renderToggle={(opened_1, toggle_0) => <StyledLines disabled={!!connection_1.disabled_at} paused={!!connection_1.paused_at} hover_index={opened_1 ? i : current_index ?? -1} selected={!!(opened_1 && hovered?.selected || hovered?.selected)} index={i} group_by={group_by} has_more={connections.length - 1 > i} onClick={() => {
                toggle_0(!opened_1);
              }} {...!opened_1 ? getHoverProps(connection_1) : {}}><div />{group_by === "source" && <div><div /><div /><div /></div>}<div><StyledConnectionWrapper><Div flex={true} style={{
                      width: `calc(100% - 32px - ${connection_1.paused_at || connection_1.disabled_at ? "40px" : "0px"} - ${(connection_1.rules.length ?? 0) * 20}px)`
                    }}>{connection_1.name && <ConnectionName name={connection_1.name} />}</Div><Div flex={{
                      gap: 2
                    }} m={group_by === "source" ? {
                      r: 1
                    } : {}}>{connection_1.paused_at && <StyledConnectionBadge p={1} warning={true}><Icon icon="pause_circle" small={true} pointer={true} /></StyledConnectionBadge>}{connection_1.disabled_at && <StyledConnectionBadge p={1}><Icon icon="disable" small={true} pointer={true} /></StyledConnectionBadge>}{getOrderedRules(connection_1.rules).length > 0 && <StyledConnectionBadge p={connection_1.rules.length === 1 ? 1 : {
                        y: 1,
                        x: 2
                      }} flex={{
                        gap: 1
                      }}>{connection_1.rules.reduce(_temp, [] as Rule[]).map(_temp2)}</StyledConnectionBadge>}</Div></StyledConnectionWrapper></div>{group_by === "destination" && <div><div /><div /><div /></div>}</StyledLines>}><ConnectionPreview connection={connection_1} onDirtyChange={handleDirtyChange} mutate={mutate} /></Dropdown></GridUnit><GridUnit size={0.4}>{(group_by !== "destination" || i === 0) && <Div onMouseEnter={() => setHovered({
                destination_id: connection_1.destination.id
              })} onMouseLeave={() => setHovered(null)}><Dropdown parent_width={{
                  min: 408
                }} placement="bottom-end" renderToggle={(opened_2, toggle_1) => <Div style={{
                  position: "relative"
                }}><StyledArrow highlighted={!!(group_by === "destination" ? !!hovered_connection && hovered_connection.destination.id === connection_1.destination.id : hovered && connection_1.id === hovered.id)} selected={!!(group_by === "destination" ? hovered?.selected : hovered && connection_1.id === hovered.id && hovered.selected)} width="6" height="8" viewBox="0 0 8 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.5 4.13397C8.16667 4.51887 8.16667 5.48113 7.5 5.86603L1.5 9.33013C0.833334 9.71503 -4.47338e-07 9.2339 -4.13689e-07 8.4641L-1.10848e-07 1.5359C-7.71986e-08 0.766098 0.833333 0.284973 1.5 0.669873L7.5 4.13397Z" /></StyledArrow><StyledCardWrapper focused={opened_2} disabled={!!connection_1.destination.disabled_at} highlighted={!!(group_by === "destination" ? !!hovered_connection && hovered_connection.destination.id === connection_1.destination.id : hovered && connection_1.id === hovered.id)} selected={!!(group_by === "destination" ? hovered?.selected : hovered && connection_1.id === hovered.id && hovered.selected)}><DestinationCard destination={connection_1.destination} onClick={() => toggle_1(!opened_2)} /></StyledCardWrapper></Div>}><DestinationPreview destination={connection_1.destination} /></Dropdown></Div>}</GridUnit></Grid><Div key={connection_1.id} p={{
            b: 4
          }} /></Div><Div m={{
          l: 3
        }} flex={{
          gap: 3
        }}><Div><MetricsPreview data_2h_sample={timeseries_by_connection_id["2h"][connection_1.id] || []} data_4h_sample={timeseries_by_connection_id["4h"][connection_1.id] || []} connection={connection_1} /></Div><Div flex={true}><Button large={true} ghost={true} icon="open" to={`/connections/${connection_1.id}`} /></Div></Div></Div>;
      $[31] = connections.length;
      $[32] = current_index;
      $[33] = group_by;
      $[34] = hovered;
      $[35] = hovered_connection;
      $[36] = mutate;
      $[37] = showDialog;
      $[38] = timeseries_by_connection_id;
      $[39] = t9;
    } else {
      t9 = $[39];
    }
    t8 = connections.map(t9);
    $[22] = connections;
    $[23] = current_index;
    $[24] = group_by;
    $[25] = hovered;
    $[26] = hovered_connection;
    $[27] = mutate;
    $[28] = showDialog;
    $[29] = timeseries_by_connection_id;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[40] !== t8) {
    t9 = <GridUnit size={0.75}>{t8}</GridUnit>;
    $[40] = t8;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  let t10;
  if ($[42] !== t7 || $[43] !== t9) {
    t10 = <Grid>{t7}{t9}</Grid>;
    $[42] = t7;
    $[43] = t9;
    $[44] = t10;
  } else {
    t10 = $[44];
  }
  return t10;
};
export default GroupedConnections;
function _temp(rules, rule) {
  if (!rules.some(t0 => {
    const {
      type
    } = t0;
    return rule.type === type;
  })) {
    rules.push(rule);
  }
  return rules;
}
function _temp2(rule_0) {
  return <StyledRuleIcon key={rule_0.type} icon={RULE_TYPES[rule_0.type].icon} pointer={true} small={true} />;
}